import { HttpErrorResponse } from '@angular/common/http';

import { MicrophoneFailure, VoiceSupport } from './voice-recorder';

/**
 * Put a transcript into a message box at the caret, the way typing would, and fire `input` so every
 * binding style the composers use (`ngModel`, `[value]` with `(input)`, a signal fed by `(input)`)
 * picks it up without the host writing any glue. A space separates it from text already there.
 */
export function insertTranscript(field: HTMLTextAreaElement | HTMLInputElement, transcript: string): void {
    const text = transcript.trim();
    if (!text) {
        return;
    }
    const start = field.selectionStart ?? field.value.length;
    const end = field.selectionEnd ?? start;
    const before = field.value.slice(0, start);
    const after = field.value.slice(end);
    const lead = before && !/\s$/.test(before) ? ' ' : '';
    const trail = after && !/^\s/.test(after) ? ' ' : '';
    field.setRangeText(`${lead}${text}${trail}`, start, end, 'end');
    field.dispatchEvent(new Event('input', { bubbles: true }));
}

/** `m:ss`, the recording timer. Digits stay Western in both locales, matching the rest of the app. */
export function formatVoiceClock(ms: number): string {
    const total = Math.max(0, Math.floor(ms / 1000));
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** English Transloco keys for each failure, shown in the button's message bubble. */
export const VOICE_MESSAGES = {
    insecure: 'Voice input needs a secure (HTTPS) connection.',
    unsupported: 'This browser cannot record audio. Try a current version of Chrome, Edge, Firefox or Safari.',
    denied: 'Microphone access is blocked. Allow it in your browser site settings, then try again.',
    noDevice: 'No microphone was found. Connect one and try again.',
    busy: 'The microphone is being used by another app.',
    micFailed: 'The microphone could not be started.',
    tooShort: 'That recording was too short. Tap the microphone, speak, then tap stop.',
    silent: 'We could not hear anything. Check that your microphone is not muted.',
    noSpeech: 'No speech was recognised. Try again a little closer to the microphone.',
    tooLong: 'That recording is too long. Keep voice messages under 2 minutes.',
    format: 'This recording format is not supported.',
    unreadable: 'The recording could not be read. Please try again.',
    rateLimited: 'Too many voice messages in a row. Wait a moment and try again.',
    quota: 'Your plan has no AI allowance left for voice input.',
    notAvailable: 'Voice input is not available in this chat.',
    network: 'The connection dropped while sending your recording.',
    timeout: 'Transcription is taking too long right now.',
    outage: 'Voice input is temporarily unavailable.',
    signIn: 'Sign in to use voice input.',
    failed: 'Something went wrong with voice input.',
} as const;

export type VoiceMessageKey = (typeof VOICE_MESSAGES)[keyof typeof VOICE_MESSAGES];

export function supportMessage(support: VoiceSupport): VoiceMessageKey | null {
    if (support === 'insecure') {
        return VOICE_MESSAGES.insecure;
    }
    return support === 'unsupported' ? VOICE_MESSAGES.unsupported : null;
}

export function microphoneMessage(failure: MicrophoneFailure): VoiceMessageKey {
    switch (failure) {
        case 'denied':
            return VOICE_MESSAGES.denied;
        case 'no-device':
            return VOICE_MESSAGES.noDevice;
        case 'busy':
            return VOICE_MESSAGES.busy;
        case 'insecure':
            return VOICE_MESSAGES.insecure;
        default:
            return VOICE_MESSAGES.micFailed;
    }
}

export interface UploadFailure {
    message: VoiceMessageKey;
    /** Whether sending the same clip again could work, so the clip is kept and Retry offered. */
    retryable: boolean;
}

/** Map a failed transcription request to what the user is told, keyed on the server's stable codes. */
export function uploadFailure(err: unknown): UploadFailure {
    if (!(err instanceof HttpErrorResponse)) {
        return { message: VOICE_MESSAGES.failed, retryable: true };
    }
    const code = (err.error as { code?: string } | null)?.code ?? '';
    if (err.status === 0) {
        return { message: VOICE_MESSAGES.network, retryable: true };
    }
    if (code.startsWith('QUOTA_')) {
        return { message: VOICE_MESSAGES.quota, retryable: false };
    }
    switch (code) {
        case 'SPEECH_NO_SPEECH':
            return { message: VOICE_MESSAGES.noSpeech, retryable: false };
        case 'SPEECH_EMPTY_AUDIO':
            return { message: VOICE_MESSAGES.tooShort, retryable: false };
        case 'SPEECH_TOO_LONG':
        case 'SPEECH_TOO_LARGE':
            return { message: VOICE_MESSAGES.tooLong, retryable: false };
        case 'SPEECH_UNSUPPORTED_FORMAT':
        case 'SPEECH_FORMAT_MISMATCH':
            return { message: VOICE_MESSAGES.format, retryable: false };
        case 'SPEECH_UNREADABLE_AUDIO':
            return { message: VOICE_MESSAGES.unreadable, retryable: false };
        case 'SPEECH_RATE_LIMITED':
            return { message: VOICE_MESSAGES.rateLimited, retryable: true };
        case 'SPEECH_NOT_AVAILABLE':
            return { message: VOICE_MESSAGES.notAvailable, retryable: false };
    }
    if (err.status === 401) {
        return { message: VOICE_MESSAGES.signIn, retryable: false };
    }
    if (err.status === 413) {
        return { message: VOICE_MESSAGES.tooLong, retryable: false };
    }
    if (err.status === 504) {
        return { message: VOICE_MESSAGES.timeout, retryable: true };
    }
    if (err.status >= 500) {
        return { message: VOICE_MESSAGES.outage, retryable: true };
    }
    return { message: VOICE_MESSAGES.failed, retryable: false };
}
